import { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, Phone, Lock, User, Eye, EyeOff, ArrowRight, ArrowLeft, Loader2, Info, ShieldCheck } from 'lucide-react';
import api from '@/api';
import { LOGO_URL } from '@/constants/categories';
import { CountryCodePicker } from '@/components/auth/CountryCodePicker';
import { OTPVerification } from '@/components/auth/OTPVerification';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

function LoginOTPStep({ userId, emailHint, identifier, onVerified, onBack }) {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resending, setResending] = useState(false);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  const handleVerify = useCallback(async (otpCode) => {
    if (otpCode.length !== 6) return;
    setError('');
    setLoading(true);
    try {
      const result = await api.verifyLoginOTP({ user_id: userId, otp: otpCode });
      onVerified(result);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid code. Please try again.');
      setCode('');
    } finally { setLoading(false); }
  }, [userId, onVerified]);

  const handleResend = async () => {
    setResending(true);
    setError('');
    try {
      await api.login({ identifier, password: '__resend_otp__' });
    } catch {
      // Re-login will fail on password but we can use a dedicated resend
      // For now just show cooldown
    }
    setResendCooldown(30);
    setResending(false);
  };

  return (
    <motion.div key="login-otp" data-testid="login-otp-screen" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="w-full max-w-md text-center">
      <ShieldCheck size={48} className="mx-auto text-blue-400 mb-4" />
      <h2 className="text-2xl font-bold text-white mb-2">Check your email</h2>
      <p className="text-gray-400 mb-6">We sent a 6-digit code to <span className="text-white font-semibold">{emailHint}</span></p>
      <div className="flex justify-center mb-4">
        <InputOTP maxLength={6} value={code} onChange={(val) => { setCode(val); if (val.length === 6) handleVerify(val); }}>
          <InputOTPGroup>
            {[0, 1, 2, 3, 4, 5].map(i => <InputOTPSlot key={i} index={i} />)}
          </InputOTPGroup>
        </InputOTP>
      </div>
      {error && <p className="text-red-400 text-sm mb-3">{error}</p>}
      {loading && <Loader2 className="animate-spin text-gray-400 mx-auto mb-3" size={24} />}
      <p className="text-gray-500 text-sm mb-4">
        Code expires in 5 minutes. {resendCooldown > 0
          ? <span className="text-gray-600">Resend in {resendCooldown}s</span>
          : <button onClick={handleResend} className="text-blue-400 hover:underline" disabled={resending}>{resending ? 'Sending...' : 'Resend code'}</button>
        }
      </p>
      <button onClick={onBack} className="text-gray-500 hover:text-gray-300 text-sm flex items-center gap-1 mx-auto">
        <ArrowLeft size={14} /> Back to login
      </button>
    </motion.div>
  );
}


export default function AuthScreen({ onAuthSuccess, onForgotPassword }) {
  const [mode, setMode] = useState('login');
  const [authMethod, setAuthMethod] = useState('email');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [mobile, setMobile] = useState('');
  const [countryCode, setCountryCode] = useState('+44');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showPwdInfo, setShowPwdInfo] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [otpStep, setOtpStep] = useState(false);
  const [otpIdentifier, setOtpIdentifier] = useState('');
  const [otpMasked, setOtpMasked] = useState('');
  const [loginUserId, setLoginUserId] = useState('');
  const [loginOtpStep, setLoginOtpStep] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [captchaFailed, setCaptchaFailed] = useState(false);
  const captchaContainerId = useRef(`recaptcha-register-${Date.now()}`);
  const captchaWidgetId = useRef(null);

  useEffect(() => {
    if (mode !== 'register') {
      // Cleanup: reset captcha when leaving register mode
      if (captchaWidgetId.current !== null && window.grecaptcha && window.grecaptcha.reset) {
        try { window.grecaptcha.reset(captchaWidgetId.current); } catch (e) { /* ignore */ }
      }
      captchaWidgetId.current = null;
      setCaptchaToken(null);
      setCaptchaFailed(false);
      // New unique ID for next render
      captchaContainerId.current = `recaptcha-register-${Date.now()}`;
      return;
    }

    setCaptchaFailed(false);
    let attempts = 0;
    const tryRender = () => {
      const el = document.getElementById(captchaContainerId.current);
      if (!el) return false;
      if (captchaWidgetId.current !== null) return true; // already rendered
      if (!window.__recaptchaReady || !window.grecaptcha || !window.grecaptcha.render) return false;
      try {
        captchaWidgetId.current = window.grecaptcha.render(el, {
          sitekey: RECAPTCHA_SITE_KEY,
          callback: (token) => { setCaptchaToken(token); setCaptchaFailed(false); },
          'expired-callback': () => setCaptchaToken(null),
          'error-callback': () => setCaptchaFailed(true),
          theme: 'dark',
          size: 'normal',
        });
        setCaptchaFailed(false);
        return true;
      } catch (e) {
        console.error('[reCAPTCHA] render error:', e);
        setCaptchaFailed(true);
        return true; // stop retrying
      }
    };

    const interval = setInterval(() => {
      attempts++;
      if (tryRender() || attempts > 40) {
        if (attempts > 40 && captchaWidgetId.current === null) {
          console.warn('[reCAPTCHA] Failed to load after 8s.', {
            scriptReady: window.__recaptchaReady,
            grecaptchaExists: !!window.grecaptcha,
            renderExists: !!(window.grecaptcha && window.grecaptcha.render),
            siteKey: RECAPTCHA_SITE_KEY ? 'set' : 'MISSING',
            containerId: captchaContainerId.current,
            containerInDom: !!document.getElementById(captchaContainerId.current),
          });
          setCaptchaFailed(true);
        }
        clearInterval(interval);
      }
    }, 200);
    return () => clearInterval(interval);
  }, [mode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (mode === 'register') {
        if (!name.trim()) { setError('Name is required'); setLoading(false); return; }
        if (!email.trim()) { setError('Email is required'); setLoading(false); return; }
        if (!mobile.trim()) { setError('Mobile number is required'); setLoading(false); return; }
        if (password.length < 8) { setError('Password must be at least 8 characters'); setLoading(false); return; }
        if (!/[A-Z]/.test(password)) { setError('Password must contain at least 1 uppercase letter'); setLoading(false); return; }
        if (!/[^A-Za-z0-9]/.test(password)) { setError('Password must contain at least 1 symbol'); setLoading(false); return; }
        if (password !== confirmPassword) { setError('Passwords do not match'); setLoading(false); return; }
        if (!captchaToken) { setError(captchaFailed ? 'CAPTCHA could not load — check domain authorization in reCAPTCHA console' : 'Please complete the CAPTCHA checkbox'); setLoading(false); return; }
        const result = await api.sendOTP({ name: name.trim(), email: email.trim(), mobile: `${countryCode}${mobile.trim()}`, password, captcha_token: captchaToken });
        setOtpIdentifier(email.trim());
        setOtpMasked(result.masked_identifier);
        setOtpStep(true);
      } else {
        const identifier = authMethod === 'email' ? email.trim() : mobile.trim();
        if (!identifier) { setError('Email or mobile is required'); setLoading(false); return; }
        if (!password) { setError('Password is required'); setLoading(false); return; }
        const result = await api.login({ identifier, password });
        if (result.status === 'otp_required') {
          setLoginUserId(result.user_id);
          setOtpMasked(result.email_hint);
          setOtpIdentifier(identifier);
          setLoginOtpStep(true);
        } else {
          localStorage.setItem('wtf_token', result.token);
          localStorage.setItem('wtf_user', JSON.stringify(result.user));
          onAuthSuccess(result.user);
        }
      }
    } catch (err) {
      // Log full error for debugging (visible in browser console)
      console.error('Auth error:', err.response?.status, err.response?.data || err.message);
      // Extract the most useful error message
      if (err.response?.data?.detail) {
        const detail = err.response.data.detail;
        // FastAPI validation errors return detail as array
        if (Array.isArray(detail)) {
          setError(detail.map(d => d.msg || d.message || JSON.stringify(d)).join('. '));
        } else {
          setError(detail);
        }
      } else if (err.response?.status) {
        const status = err.response.status;
        const msgs = { 409: 'Account already exists with this email or mobile', 403: 'CAPTCHA verification failed — please try again', 429: 'Too many attempts — please wait a minute', 500: 'Server error — please try again later', 502: 'Server is starting up — please try again in a moment' };
        setError(msgs[status] || `Server error (${status})`);
      } else if (err.code === 'ERR_NETWORK' || !err.response) {
        setError('Cannot reach the server — check your connection and try again');
      } else {
        setError('Something went wrong — please try again');
      }
    } finally { setLoading(false); }
  };

  const handleOTPVerified = (result) => {
    localStorage.setItem('wtf_token', result.token);
    localStorage.setItem('wtf_user', JSON.stringify(result.user));
    onAuthSuccess(result.user);
  };

  return (
    <div data-testid="auth-screen" className="min-h-screen flex flex-col items-center justify-center px-5 py-10">
      <AnimatePresence mode="wait">
        {loginOtpStep ? (
          <LoginOTPStep key="login-otp" userId={loginUserId} emailHint={otpMasked} identifier={otpIdentifier}
            onVerified={handleOTPVerified} onBack={() => { setLoginOtpStep(false); setError(''); }} />
        ) : otpStep ? (
          <OTPVerification key="otp" identifier={otpIdentifier} maskedIdentifier={otpMasked}
            onVerified={handleOTPVerified} onBack={() => { setOtpStep(false); setError(''); }} />
        ) : (
          <motion.div key="form" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="w-full max-w-md">
            <div className="text-center mb-10">
              <motion.img src={LOGO_URL} alt="WTFHappened" initial={{ scale: 0.8 }} animate={{ scale: 1 }}
                transition={{ type: 'spring', stiffness: 200 }} className="h-36 sm:h-40 mx-auto mb-5 object-contain" />
              <p className="text-lg text-gray-500 mt-1">{mode === 'login' ? 'Welcome back' : 'Create your account'}</p>
            </div>

            {mode === 'login' && (
              <div className="flex bg-gray-100 rounded-2xl p-1.5 mb-6">
                <button data-testid="auth-method-email" onClick={() => setAuthMethod('email')}
                  className={`flex-1 py-3 rounded-xl text-base font-semibold transition-all duration-200 ${authMethod === 'email' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'}`}>
                  <Mail size={16} className="inline mr-2" />Email
                </button>
                <button data-testid="auth-method-mobile" onClick={() => setAuthMethod('mobile')}
                  className={`flex-1 py-3 rounded-xl text-base font-semibold transition-all duration-200 ${authMethod === 'mobile' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'}`}>
                  <Phone size={16} className="inline mr-2" />Mobile
                </button>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <AnimatePresence>
                {mode === 'register' && (
                  <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}>
                    <div className="relative">
                      <User size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input data-testid="auth-name" type="text" value={name} onChange={e => setName(e.target.value)}
                        placeholder="Your name" className="w-full pl-12 pr-4 py-4 rounded-2xl bg-white border border-gray-200 text-base text-gray-900 placeholder:text-gray-400 outline-none focus:border-gray-400 focus:shadow-sm transition-all" />
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {(mode === 'register' || authMethod === 'email') && (
                <div className="relative">
                  <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input data-testid="auth-email" type="email" value={email} onChange={e => setEmail(e.target.value)}
                    placeholder="Email address" className="w-full pl-12 pr-4 py-4 rounded-2xl bg-white border border-gray-200 text-base text-gray-900 placeholder:text-gray-400 outline-none focus:border-gray-400 focus:shadow-sm transition-all" />
                </div>
              )}

              {(mode === 'register' || (mode === 'login' && authMethod === 'mobile')) && (
                <div className="flex">
                  {mode === 'register' && <CountryCodePicker value={countryCode} onChange={setCountryCode} />}
                  <div className="relative flex-1">
                    {mode === 'login' && <Phone size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />}
                    <input data-testid="auth-mobile" type="tel" value={mobile} onChange={e => setMobile(e.target.value)}
                      placeholder="Mobile number"
                      className={`w-full pr-4 py-4 bg-white border border-gray-200 text-base text-gray-900 placeholder:text-gray-400 outline-none focus:border-gray-400 focus:shadow-sm transition-all ${mode === 'register' ? 'pl-4 rounded-r-2xl border-l-0' : 'pl-12 rounded-2xl'}`} />
                  </div>
                </div>
              )}

              <div className="relative">
                <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                <input data-testid="auth-password" type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)}
                  placeholder="Password" className="w-full pl-12 pr-20 py-4 rounded-2xl bg-white border border-gray-200 text-base text-gray-900 placeholder:text-gray-400 outline-none focus:border-gray-400 focus:shadow-sm transition-all" />
                <div className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center gap-2">
                  {mode === 'register' && (
                    <button type="button" data-testid="password-info-toggle" onClick={() => setShowPwdInfo(!showPwdInfo)} className="text-gray-400 hover:text-gray-600 transition-colors">
                      <Info size={17} />
                    </button>
                  )}
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="text-gray-400 hover:text-gray-600">
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <AnimatePresence>
                {mode === 'register' && showPwdInfo && (
                  <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden">
                    <div className="px-4 py-3 rounded-xl bg-gray-50 border border-gray-100 text-sm space-y-1.5" data-testid="password-requirements">
                      <p className="text-gray-500 font-medium text-xs uppercase tracking-wide mb-1">Password must have:</p>
                      {[
                        { check: password.length >= 8, label: 'At least 8 characters' },
                        { check: /[A-Z]/.test(password), label: 'At least 1 uppercase letter' },
                        { check: /[^A-Za-z0-9]/.test(password), label: 'At least 1 symbol (!@#$...)' },
                      ].map(r => (
                        <div key={r.label} className="flex items-center gap-2">
                          <div className={`w-1.5 h-1.5 rounded-full ${r.check ? 'bg-green-500' : 'bg-gray-300'}`} />
                          <span className={r.check ? 'text-green-600' : 'text-gray-400'}>{r.label}</span>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {mode === 'register' && (
                <div className="relative">
                  <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input data-testid="auth-confirm-password" type={showPassword ? 'text' : 'password'} value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
                    placeholder="Confirm Password" className="w-full pl-12 pr-12 py-4 rounded-2xl bg-white border border-gray-200 text-base text-gray-900 placeholder:text-gray-400 outline-none focus:border-gray-400 focus:shadow-sm transition-all" />
                </div>
              )}

              <AnimatePresence>
                {error && (
                  <motion.p initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                    className="text-red-500 text-sm text-center py-1" data-testid="auth-error">{error}</motion.p>
                )}
              </AnimatePresence>

              {mode === 'register' && (
                <div className="flex flex-col items-center my-3" data-testid="recaptcha-container">
                  <div id={captchaContainerId.current} />
                  {captchaFailed && (
                    <div className="mt-2 px-4 py-2 rounded-lg bg-amber-900/30 border border-amber-700/40 text-center">
                      <p className="text-amber-400 text-xs font-semibold">CAPTCHA failed to load</p>
                      <p className="text-amber-400/70 text-[10px] mt-0.5">Check that this domain is authorized in reCAPTCHA admin console</p>
                    </div>
                  )}
                </div>
              )}

              <button data-testid="auth-submit" type="submit" disabled={loading || (mode === 'register' && !captchaToken)}
                className="w-full py-4 rounded-2xl bg-gray-900 text-white text-base font-bold flex items-center justify-center gap-2 hover:bg-gray-800 disabled:opacity-60 transition-all duration-200">
                {loading ? <Loader2 size={20} className="animate-spin" /> : <>{mode === 'login' ? 'Sign In' : 'Create Account'}<ArrowRight size={18} /></>}
              </button>

              {mode === 'register' && (
                <p className="text-sm text-gray-400 text-center mt-3">
                  By signing up, you accept our{' '}
                  <button data-testid="terms-link" onClick={() => window.open('/terms', '_blank')} className="text-gray-600 underline hover:text-gray-900">Terms & Conditions</button>
                </p>
              )}

              {mode === 'login' && (
                <button data-testid="forgot-password-link" type="button" onClick={onForgotPassword}
                  className="w-full text-center text-sm text-gray-400 hover:text-gray-600 mt-3 transition-colors">Forgot password?</button>
              )}
            </form>

            <p className="text-center text-base text-gray-400 mt-6">
              {mode === 'login' ? "Don't have an account?" : 'Already have an account?'}
              <button data-testid="auth-toggle-mode" onClick={() => { setMode(mode === 'login' ? 'register' : 'login'); setError(''); }}
                className="ml-1.5 text-gray-900 font-bold hover:underline">
                {mode === 'login' ? 'Sign Up' : 'Sign In'}
              </button>
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
